// src/utils/aoiBounds.js
//
// Shared AOI geometry utilities.
// Extracted from MapPage.jsx so AoiPanel and other consumers
// can derive bounding boxes without duplicating logic.

/**
 * Derive flat [minX, minY, maxX, maxY] from a raw GeoJSON geometry or Feature.
 * Returns null if geom is absent or has no parseable coordinates.
 *
 * @param {object|null} geom - GeoJSON Geometry or Feature
 * @returns {[number,number,number,number]|null}
 */
export function aoiBoundsFlat(geom) {
  if (!geom) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  const consume = ([x, y]) => {
    if (typeof x !== "number" || typeof y !== "number") return;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  };

  const walk = (c) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === "number") consume(c); else c.forEach(walk);
  };

  const geometry = geom.type === "Feature" ? geom.geometry : geom;
  if (!geometry?.coordinates) return null;
  walk(geometry.coordinates);
  return isFinite(minX) ? [minX, minY, maxX, maxY] : null;
}

/**
 * Derive flat [minX, minY, maxX, maxY] from a layer object whose geometry
 * lives on `.geojson` or `.data` — a GeoJSON FeatureCollection or a single
 * Feature. Returns null if no parseable coordinates are present.
 *
 * The user-imported / drawn layers in My Layers carry their geometry this
 * way, unlike AOIs which carry a bare geometry on `.geom` (use aoiBoundsFlat
 * for those).
 *
 * @param {object|null} layer - object with a `.geojson` or `.data` field
 * @returns {[number,number,number,number]|null}
 */
export function layerBoundsFlat(layer) {
  const geojson = layer?.geojson ?? layer?.data;
  if (!geojson) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  const consume = ([x, y]) => {
    if (typeof x !== "number" || typeof y !== "number") return;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  };

  const walk = (c) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === "number") consume(c); else c.forEach(walk);
  };

  const features =
    geojson.features ?? (geojson.type === "Feature" ? [geojson] : []);
  features.forEach((f) => {
    if (f?.geometry?.coordinates) walk(f.geometry.coordinates);
  });

  return isFinite(minX) ? [minX, minY, maxX, maxY] : null;
}

/**
 * True iff two flat bboxes [minX, minY, maxX, maxY] overlap (touching counts).
 * Used by the LayerPanel tabs to mark AOIs / user layers whose extent
 * intersects the active operating AOI's bounds.
 *
 * @param {[number,number,number,number]|null} a
 * @param {[number,number,number,number]|null} b
 * @returns {boolean}
 */
export function bboxesIntersect(a, b) {
  if (!a || !b) return false;
  return !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
}

/**
 * Compute the combined extent across all AOIs that have geometry.
 * Returns flat [minX, minY, maxX, maxY] or null.
 *
 * @param {Array<{geom: object|null}>} aois
 * @returns {[number,number,number,number]|null}
 */
export function combinedAoiBounds(aois) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  aois.forEach(aoi => {
    const b = aoiBoundsFlat(aoi.geom);
    if (!b) return;
    if (b[0] < minX) minX = b[0]; if (b[2] > maxX) maxX = b[2];
    if (b[1] < minY) minY = b[1]; if (b[3] > maxY) maxY = b[3];
  });
  return isFinite(minX) ? [minX, minY, maxX, maxY] : null;
}
